import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { ControlsOf, FormArray, FormGroup } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  of,
  Subject,
  switchMap,
  timer,
} from 'rxjs';
import { v7 as uuid } from 'uuid';
import {
  FormQuestion,
  FormQuestionRepeater,
  FormRepeatable,
} from '../../../core/models/form-group-mapping.model';
import { ReadonlyState } from '../../../core/models/form.model';
import {
  QuestionStructureRepeater,
  Repeatable,
} from '../../../core/models/question/question-repeater.model';
import { Question } from '../../../core/models/question/question.model';
import { RendererFormsQuery } from '../../../core/queries/renderer-forms.query';
import { DynamicFormControlService } from '../../../core/services/dynamic-form-control.service';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { QuestionNumberService } from '../../../core/services/question-number.service';
import { QuestionValueChangeService } from '../../../core/services/question-value-change.service';

@UntilDestroy()
@Component({
  selector: 'lib-question-repeater',
  templateUrl: './question-repeater.component.html',
  styleUrls: ['./question-repeater.component.scss'],
  standalone: false,
})
export class QuestionRepeaterComponent implements OnInit {
  @Input() showAllFollowUps: boolean;
  @Input() questionForm: FormGroup<ControlsOf<FormQuestionRepeater>>;

  errorText: string;
  helpText$: Observable<string>;
  activeLang$: Observable<string>;
  errorMessage$: Observable<string>;
  buttonBlur = new BehaviorSubject<boolean>(false);
  buttonBlur$ = this.buttonBlur.asObservable();
  repeaterDeleted = new BehaviorSubject<boolean>(false);
  repeaterDeleted$ = this.repeaterDeleted.asObservable();
  repeaterChanged = new BehaviorSubject<void>(undefined);
  repeaterChanged$ = this.repeaterChanged.asObservable();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  repeaterQuestions$: Observable<any[]>;
  readOnly$: Observable<ReadonlyState>;

  private _question: Question;

  @Input() set question(value: Question) {
    this._question = value;
    this.questionNumber$ = this.questionNumberService.getQuestionNumberForLabel(
      this.question.id,
    );
    this.repeaterChanged.next();
  }

  get question() {
    return this._question;
  }

  questionNumber$: Observable<unknown>;

  get id(): string {
    return this.question.id;
  }

  get description(): string {
    return this.structure.description;
  }

  get structure() {
    return this.question.structure as QuestionStructureRepeater;
  }

  get numberOfRepeatables(): number {
    return this.repeatablesControl.length;
  }

  get maxNumberOfRepeatables(): number {
    return this.structure.maxNumberOfRepeatables;
  }

  get minNumberOfRepeatables(): number {
    return this.structure.minNumberOfRepeatables;
  }

  get repeatables() {
    return this.questionForm.get('repeatables');
  }

  get repeatablesControl() {
    return this.repeatables?.controls ?? [];
  }

  get showAddRepeatableButton() {
    return (
      !this.maxNumberOfRepeatables ||
      this.numberOfRepeatables < this.maxNumberOfRepeatables
    );
  }

  get questionsOrder() {
    return this.structure.repeaterQuestionsOrder;
  }

  repeaterEmptied = new Subject<boolean>();
  hasHadErrors: boolean;
  repeatableQuestions(
    repeatable: FormGroup<ControlsOf<FormRepeatable>>,
  ): FormArray<FormQuestion> {
    return repeatable.get('questions');
  }
  constructor(
    private dynamicFormControlService: DynamicFormControlService,
    private questionNumberService: QuestionNumberService,
    private translocoService: TranslocoService,
    private questionValueChangeService: QuestionValueChangeService,
    private formsQuery: RendererFormsQuery,
    private formValidationService: FormValidationService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.readOnly$ = this.formsQuery
      .selectReadOnly()
      .pipe(distinctUntilChanged());

    this.setErrorMessage();

    this.activeLang$ = this.translocoService.langChanges$;

    this.questionValueChangeService
      .valueChangeListener(
        this.questionForm.get('value'),
        {
          questionId: this.question.id,
        },
        this.question,
      )
      .pipe(untilDestroyed(this))
      .subscribe();

    this.questionValueChangeService
      .patchValueListener(
        this.question,
        undefined,
        this.questionForm.get('value'),
      )
      .pipe(untilDestroyed(this))
      .subscribe(() => this.addRepeatablesFromValue());

    this.questionValueChangeService.patchInitialValue(
      this.question.id,
      undefined,
      this.questionForm.get('value'),
      this.question,
    );

    this.addRepeatablesFromValue();

    this.helpText$ = timer(0).pipe(
      switchMap(() => this.repeaterChanged$),
      switchMap(() => this.createHelpText()),
    );
    this.repeaterQuestions$ = this.repeaterChanged$.pipe(
      map(() => this.setRepeaterQuestions()),
    );
  }

  /**
   * Add a new repeater and be sure it does not have any values.
   */
  addRepeatable() {
    const repeatable = {
      id: uuid(),
      repeatableQuestions: this.questionsOrder.flat().map((qId) => {
        return {
          id: qId,
        };
      }),
    } as Repeatable;
    const repeaterFormControl =
      this.dynamicFormControlService.createRepeatableFormGroup(repeatable);
    this.repeatables.push(repeaterFormControl);

    if (this.repeatables.length === this.minNumberOfRepeatables) {
      this.errorText = '';
    }
    this.repeaterEmptied.next(true);

    this.repeaterChanged.next();
    this.formValidationService.triggerSectionInvalidCheck(this.question.id);
    this.setRepeatableValue();
  }

  addRepeatablesFromValue() {
    if (!this.questionForm.value.value) {
      return;
    }
    const repeatables = JSON.parse(this.questionForm.value.value) as string[];
    const newRepeatables = [];
    for (const repeatableId of repeatables) {
      const existingRepeatable = this.repeatables.controls.find(
        (r) => r.value.id === repeatableId,
      );
      if (existingRepeatable) {
        newRepeatables.push(existingRepeatable);
        continue;
      }

      const repeatable = {
        id: repeatableId,
        repeatableQuestions: this.questionsOrder.flat().map((qId) => {
          return {
            id: qId,
          };
        }),
      } as Repeatable;
      const repeaterFormControl =
        this.dynamicFormControlService.createRepeatableFormGroup(repeatable);
      newRepeatables.push(repeaterFormControl);
    }
    this.repeatables.clear();
    for (const newRepeatable of newRepeatables) {
      this.repeatables.push(newRepeatable);
    }

    if (this.repeatables.length === this.minNumberOfRepeatables) {
      this.errorText = '';
    }
    this.repeaterChanged.next();
    this.formValidationService.triggerSectionInvalidCheck(this.question.id);
    this.cdr.markForCheck();
  }

  deleteRepeatable(index: number) {
    if (index < this.repeatables.length - 1) {
      const value = this.repeatables.getRawValue();
      this.repeatables.setValue(
        value
          .slice(0, index)
          // eslint-disable-next-line unicorn/prefer-spread
          .concat(value.slice(index + 1))
          // eslint-disable-next-line unicorn/prefer-spread
          .concat(value[index]),
      );
      this.remove(value.length - 1);
    } else {
      this.remove(index);
    }
    this.repeaterChanged.next();
    this.formValidationService.triggerSectionInvalidCheck(this.question.id);
    this.setRepeatableValue();
  }

  remove(position: number) {
    const repeatableToDeleteId = this.repeatables.at(position).value.id;
    this.repeatables.removeAt(position);
    this.questionValueChangeService.removeRepeaterGroup(repeatableToDeleteId);
    this.repeaterDeleted.next(true);
    this.repeaterEmptied.next(false);
    this.repeaterChanged.next();
  }

  getRepeaterQuestion(
    repeatableIndex: number,
    questionId: string,
  ): AbstractControl {
    const repeatable = this.repeatables.at(repeatableIndex);
    const questionIndex = repeatable.value.questions.findIndex(
      (q) => q.id === questionId,
    );
    return repeatable.get('questions').at(questionIndex);
  }

  createHelpText(): Observable<string> {
    if (
      !this.maxNumberOfRepeatables &&
      this.repeatables.length < this.minNumberOfRepeatables
    ) {
      return this.translocoService.selectTranslate('repeatables_min', {
        minNumberOfRepeatables: this.minNumberOfRepeatables,
      });
    } else if (
      this.maxNumberOfRepeatables &&
      !this.minNumberOfRepeatables &&
      this.repeatables.length < this.maxNumberOfRepeatables
    ) {
      return this.translocoService.selectTranslate('repeatables_max', {
        maxNumberOfRepeatables: this.maxNumberOfRepeatables,
      });
    } else if (
      this.showAddRepeatableButton &&
      this.maxNumberOfRepeatables &&
      this.minNumberOfRepeatables
    ) {
      return this.translocoService.selectTranslate('repeatables_min_max', {
        minNumberOfRepeatables: this.minNumberOfRepeatables,
        maxNumberOfRepeatables: this.maxNumberOfRepeatables,
      });
    } else {
      return of(undefined);
    }
  }

  private setRepeatableValue() {
    const repeatableValue = [];
    for (const repeatable of this.repeatables.controls) {
      repeatableValue.push(repeatable.value.id);
    }
    this.questionForm.get('value').patchValue(JSON.stringify(repeatableValue));
    this.questionForm.get('value').markAsDirty();
  }

  onBlur() {
    this.buttonBlur.next(true);
  }

  setRepeaterQuestions() {
    const repeaterQuestions = [];
    for (const repeatable of this.repeatables.controls) {
      const r = [];
      for (const [index, question] of repeatable.value.questions.entries()) {
        r[question.id] = repeatable.get('questions').at(index);
      }
      repeaterQuestions.push(r);
    }
    return repeaterQuestions;
  }

  setErrorMessage() {
    this.errorMessage$ = merge(
      this.repeaterEmptied,
      this.formValidationService.showErrors$.pipe(
        filter((err) => err),
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        map(() => {}),
      ),
    ).pipe(switchMap((e) => this.validateAndSetErrorMessage(e)));
  }

  private validateAndSetErrorMessage(e: boolean | void) {
    if (
      this.repeatables.length < this.minNumberOfRepeatables &&
      (!e || this.hasHadErrors)
    ) {
      this.hasHadErrors = true;
      return this.createHelpText();
    } else return of(undefined);
  }

  trackByFn(index) {
    return index;
  }
}
